import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import ImageGallery from '../../components/common/ImageGallery';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { productService } from '../../services/product.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import { formatProductPrice, getStockStatus, calculateProfitMargin } from '../../utils/productHelpers';
import { ArrowLeftIcon } from '../../components/icons/Icons';

const SellerProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productService.getById(id);
      setProduct(response.data.product);
    } catch (error) {
      toast.error('Product not found');
      navigate('/seller/products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    
    try {
      await productService.delete(product.id);
      toast.success('Product deleted');
      navigate('/seller/products');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!product) return null;

  const stockStatus = getStockStatus(product);
  const profitMargin = calculateProfitMargin(product.price, product.cost_per_item);

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
          {/* ✅ FIXED: Properly styled back button */}
          <Button variant="ghost" onClick={() => navigate('/seller/products')}>
            <ArrowLeftIcon size={18} />
            Back to Products
          </Button>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button variant="secondary" onClick={() => navigate(`/seller/products/${product.id}/edit`)}>
              Edit Product
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.xl }}>
          <div>
            <ImageGallery images={product.images} altText={product.name} />
          </div>

          <div>
            <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
              {product.name}
            </h1>

            <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.lg }}>
              <Badge variant={product.is_active ? 'success' : 'error'}>
                {product.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {product.is_featured && <Badge variant="primary">Featured</Badge>}
              <Badge variant={stockStatus.color === 'success' ? 'success' : stockStatus.color === 'warning' ? 'warning' : 'error'}>
                {stockStatus.label}
              </Badge>
            </div>

            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Pricing
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Selling Price</div>
                  <div style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main }}>
                    {formatProductPrice(product.price)}
                  </div>
                </div>
                {product.compare_price && (
                  <div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Compare Price</div>
                    <div style={{ fontSize: theme.typography.fontSize.xl, textDecoration: 'line-through', color: theme.colors.text.secondary }}>
                      {formatProductPrice(product.compare_price)}
                    </div>
                  </div>
                )}
                {product.cost_per_item && (
                  <>
                    <div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Cost</div>
                      <div style={{ fontSize: theme.typography.fontSize.lg }}>
                        {formatProductPrice(product.cost_per_item)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Profit Margin</div>
                      <div style={{ fontSize: theme.typography.fontSize.lg, color: profitMargin > 20 ? theme.colors.success : theme.colors.warning }}>
                        {profitMargin.toFixed(1)}%
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Inventory
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs }}>
                    SKU
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                    {product.sku || 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs }}>
                    Barcode
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                    {product.barcode || 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs }}>
                    Available Quantity
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                    {product.track_inventory ? product.quantity : 'Not tracked'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs }}>
                    Weight
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                    {product.weight ? `${product.weight} kg` : 'N/A'}
                  </div>
                </div>
              </div>

              {!product.track_inventory && (
                <div style={{ 
                  marginTop: theme.spacing.md, 
                  padding: theme.spacing.md, 
                  backgroundColor: theme.colors.gray[50], 
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.text.secondary,
                  textAlign: 'center'
                }}>
                  Inventory tracking is disabled for this product
                </div>
              )}

              <div style={{ marginTop: theme.spacing.md, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.gray[200]}` }}>
                <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: theme.spacing.xs }}>
                  To update inventory, please edit the product
                </div>
                <Button 
                  size="sm" 
                  variant="secondary" 
                  onClick={() => navigate(`/seller/products/${product.id}/edit`)}
                  fullWidth
                >
                  Edit Product Details
                </Button>
              </div>
            </Card>

            {product.description && (
              <Card padding="lg">
                <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                  Description
                </h3>
                <p style={{ lineHeight: 1.6, color: theme.colors.text.secondary }}>
                  {product.description}
                </p>
              </Card>
            )}

            {product.tags && product.tags.length > 0 && (
              <div style={{ marginTop: theme.spacing.lg }}>
                <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing.sm }}>
                  Tags
                </div>
                <div style={{ display: 'flex', gap: theme.spacing.xs, flexWrap: 'wrap' }}>
                  {product.tags.map((tag, index) => (
                    <Badge key={index}>{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SellerProductDetails;